import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from "react";
import { Socket } from "socket.io-client";
import { useAppSelector } from "@/Redux/app/hooks";
import { getAuthToken } from "@/lib/authCookies";
import { disconnectChatSocket, getChatSocket } from "@/lib/chatSocket";
import { NewTicketEvent } from "@/Components/Supports/types";

type NewTicketListener = (ticket: NewTicketEvent) => void;

interface ChatSocketContextValue {
    socket: Socket | null;
    isConnected: boolean;
    activeTicketId: string | null;
    setActiveTicketId: (ticketId: string | null) => void;
    subscribeNewTicket: (listener: NewTicketListener) => () => void;
}

const ChatSocketContext = createContext<ChatSocketContextValue | null>(null);

export const ChatSocketProvider = ({ children }: { children: React.ReactNode }) => {
    const userType = useAppSelector((state) => state.auth.loggedInUserDetails.userDetail?.userType);
    const [socket, setSocket] = useState<Socket | null>(null);
    const [isConnected, setIsConnected] = useState(false);
    const [activeTicketId, setActiveTicketId] = useState<string | null>(null);
    const activeTicketIdRef = useRef<string | null>(null);
    const newTicketListenersRef = useRef<Set<NewTicketListener>>(new Set());

    const setActiveTicket = useCallback((ticketId: string | null) => {
        activeTicketIdRef.current = ticketId;
        setActiveTicketId(ticketId);
    }, []);

    const subscribeNewTicket = useCallback((listener: NewTicketListener) => {
        newTicketListenersRef.current.add(listener);
        return () => {
            newTicketListenersRef.current.delete(listener);
        };
    }, []);

    useEffect(() => {
        if (userType !== "ADMIN" && userType !== "KITCHEN" && userType !== "AGENCY") {
            disconnectChatSocket();
            setSocket(null);
            setIsConnected(false);
            return;
        }

        const token = getAuthToken();
        if (!token) {
            disconnectChatSocket();
            setSocket(null);
            setIsConnected(false);
            return;
        }

        const chatSocket = getChatSocket(token);

        const handleConnect = () => {
            setIsConnected(true);
            const ticketId = activeTicketIdRef.current;
            if (ticketId) {
                chatSocket.emit("room-join", { ticketId });
            }
        };

        const handleDisconnect = () => {
            setIsConnected(false);
        };

        const handleNewTicket = (ticket: NewTicketEvent) => {
            newTicketListenersRef.current.forEach((listener) => listener(ticket));
        };

        chatSocket.on("connect", handleConnect);
        chatSocket.on("disconnect", handleDisconnect);
        chatSocket.on("new-ticket", handleNewTicket);

        setSocket(chatSocket);
        setIsConnected(chatSocket.connected);

        return () => {
            chatSocket.off("connect", handleConnect);
            chatSocket.off("disconnect", handleDisconnect);
            chatSocket.off("new-ticket", handleNewTicket);
        };
    }, [userType]);

    const value: ChatSocketContextValue = {
        socket,
        isConnected,
        activeTicketId,
        setActiveTicketId: setActiveTicket,
        subscribeNewTicket,
    };

    return <ChatSocketContext.Provider value={value}>{children}</ChatSocketContext.Provider>;
};

export const useChatSocket = (): ChatSocketContextValue => {
    const context = useContext(ChatSocketContext);
    if (!context) {
        throw new Error("useChatSocket must be used within ChatSocketProvider");
    }
    return context;
};
